import React, { useEffect } from "react";
import { Smile, Brain, HeartPlus } from "lucide-react";

const DEFAULT_STEPS = [
  {
    name: "CHECK IN",
    brainPart: "Brain Stem",
    imageSrc: "images/brain/brain1.jpg",
    imageAlt: "Check In brain step",
    icon: Smile,
  },
  {
    name: "BE MINDFUL",
    brainPart: "Midbrain",
    imageSrc: "images/brain/brain2.jpg",
    imageAlt: "Be Mindful brain step",
    icon: Brain,
  },
  {
    name: "PRACTICE",
    brainPart: "Cortex",
    imageSrc: "images/brain/brain3.jpg",
    imageAlt: "Practice brain step",
    icon: HeartPlus,
  },
];

const PracticeIntroModal = ({
  isOpen,
  onClose,
  onStart,
  steps = DEFAULT_STEPS,
}) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[1000] sm:ml-[130px] flex items-center justify-center bg-black/30 p-4 sm:items-center sm:p-10"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-[90vw] max-h-[70%] sm:max-w-[80%] sm:max-h-[60%] lg:max-w-[70%] lg:max-h-[85%] 
        2xl:max-h-[800px] 2xl:max-w-[730px] overflow-hidden rounded bg-white
        shadow-[0_20px_60px_rgba(18,45,70,0.22)] flex flex-col"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="practice-intro-heading"
      >
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="flex flex-col items-center gap-4">
            <h2
              id="practice-intro-heading"
              className="mx-4 text-xl sm:text-2xl text-center font-bold"
            >
              Before You Practice 1-2-3 Wellness,<br /> Learn How It Works Below
            </h2>

            <div className="flex flex-col gap-4 rounded bg-[#F7FAFD] px-4 py-4 sm:flex-row sm:items-center sm:gap-6 sm:px-6 sm:py-5">
              <img
                src="images/brain/3colorRRR.gif"
                alt="Brain image"
                className="h-auto w-64 flex-shrink-0 border-t border-b border-[#DDDDDD]"
              />
              <p className="m-0 text-base text-[#1E2D3D]">
                1-2-3 Wellness can help you move up your brain to learn and grow! <br /> <br />
                <strong>Regulate:</strong> Learn how to calm your mind and body.<br />
                <strong>Relate:</strong> Learn how to connect with others, and your environment.<br />
                <strong>Reason:</strong> Choose a wellness practice to get ready to learn!
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 w-full sm:w-auto">
              {steps.map((step, index) => (
                <div
                  key={step.name}
                  className="flex flex-col items-center gap-2 rounded bg-[#F9FAFB] px-8 py-5"
                >
                  <div className="relative">
                    <img
                      src={step.imageSrc}
                      alt={step.imageAlt}
                      className="h-auto w-24 flex-shrink-0 rounded object-cover"
                    />
                    <div className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#3387CF] text-sm font-bold text-white shadow-sm">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1 font-semibold">
                    <step.icon className="h-[14px] w-[14px] shrink-0" />
                    <span className="text-xs">{step.name}</span>
                  </div>
                  <p className="m-0 text-sm text-[#555555]">
                    {step.brainPart}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-center px-4 py-4 sm:px-6">
          <button
            type="button"
            className="rounded border-0 bg-[#3387CF] px-4 py-2 text-base font-semibold text-white"
            onClick={onStart}
          >
            Start Practice
          </button>
        </div>
      </div>
    </div>
  );
};

export default PracticeIntroModal;
